import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const ROLE_OPTIONS = ['Owner', 'Manager', 'Emergency', 'Tenant', 'Caretaker', 'Other'];

/** A row the user has started filling in. Untouched rows are dropped on save. */
const rowHasContent = (c) =>
  Boolean((c?.phone || '').trim() || (c?.email || '').trim());

const ContactList = ({ contacts = [], onChange }) => {
  const handleAddContact = () => {
    onChange([...contacts, { name: '', phone: '', email: '', role: 'Owner' }]);
  };

  const handleRemoveContact = (index) => {
    const updated = contacts.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleChange = (index, field, value) => {
    const updated = contacts.map((contact, i) => {
      if (i === index) {
        return { ...contact, [field]: value };
      }
      return contact;
    });
    onChange(updated);
  };

  return (
    <Box>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name *</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell width={60}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact, index) => (
              <TableRow key={index}>
                <TableCell>
                  {/* Required by the backend. Flag it here so a half-filled
                      row is obvious in place, rather than 422-ing the whole
                      member save (hazards and animals included) on submit. */}
                  <TextField
                    size="small"
                    fullWidth
                    required
                    value={contact.name || ''}
                    onChange={(e) => handleChange(index, 'name', e.target.value)}
                    placeholder="Contact name"
                    error={!(contact.name || '').trim() && rowHasContent(contact)}
                    helperText={
                      !(contact.name || '').trim() && rowHasContent(contact)
                        ? 'Name is required'
                        : ' '
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={contact.phone || ''}
                    onChange={(e) => handleChange(index, 'phone', e.target.value)}
                    placeholder="Phone number"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={contact.email || ''}
                    onChange={(e) => handleChange(index, 'email', e.target.value)}
                    placeholder="Email address"
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={contact.role || 'Owner'}
                      onChange={(e) => handleChange(index, 'role', e.target.value)}
                    >
                      {ROLE_OPTIONS.map((role) => (
                        <MenuItem key={role} value={role}>
                          {role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Tooltip title="Remove contact">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveContact(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddContact}
        sx={{ mt: 2 }}
      >
        Add Contact
      </Button>
    </Box>
  );
};

export default ContactList;
